import { useCallback, useEffect, useRef, useState } from "react";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import type { StressCard } from "./stress-test-content";

/**
 * `useCustomStressCards` — lets a facilitator add their own free-text
 * FUD/adoption cards to the Stress-Test board for any workshop,
 * persisted to `idb-keyval` keyed by `(workshopId, "stress-test:custom")`.
 *
 * Mirrors `useCardPlacement`'s hydrate/persist/StrictMode-safe pattern
 * (`./use-card-placement.ts`) but for the card *deck* rather than the
 * placement map — custom cards still flow through the same
 * `useCardPlacement` placement state once added, keyed by their id.
 */

export type UseCustomStressCardsResult = {
  customCards: readonly StressCard[];
  /** Add a new card with the given text. No-op if the text is blank. */
  addCard: (text: string) => void;
  /** Remove a previously-added custom card. */
  removeCard: (cardId: string) => void;
  /** True until the first IDB read resolves. */
  hydrated: boolean;
};

function storageKey(workshopId: string): string {
  return `${workshopId}:stress-test:custom`;
}

export function useCustomStressCards(workshopId: string): UseCustomStressCardsResult {
  const [customCards, setCustomCards] = useState<StressCard[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const hydrationToken = useRef(0);

  useEffect(() => {
    const token = ++hydrationToken.current;
    setHydrated(false);
    let cancelled = false;

    idbGet<StressCard[]>(storageKey(workshopId))
      .then((stored) => {
        if (cancelled || hydrationToken.current !== token) return;
        setCustomCards(Array.isArray(stored) ? stored : []);
        setHydrated(true);
      })
      .catch(() => {
        if (cancelled || hydrationToken.current !== token) return;
        setCustomCards([]);
        setHydrated(true);
      });

    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  useEffect(() => {
    if (!hydrated) return;
    idbSet(storageKey(workshopId), customCards).catch(() => {
      // Best-effort persistence — silently ignore write failures.
    });
  }, [hydrated, customCards, workshopId]);

  const addCard = useCallback((text: string): void => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? `custom-${crypto.randomUUID()}`
        : `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setCustomCards((prev) => [...prev, { id, text: trimmed }]);
  }, []);

  const removeCard = useCallback((cardId: string): void => {
    setCustomCards((prev) => prev.filter((c) => c.id !== cardId));
  }, []);

  return { customCards, addCard, removeCard, hydrated };
}
